import { create } from 'zustand';
import { api } from '@/lib/api';

export interface User {
  id: number;
  email: string;
  name: string;
  roles?: { name: string }[];
  permissions?: { name: string }[];
  locale?: string | null;
  timezone?: string | null;
  agency_id?: number;
  agency?: {
    id: number;
    name: string;
    slug: string;
    plan_id: number;
    brand_logo_path?: string | null;
    brand_color?: string | null;
  };
}

/**
 * Returns true when the user has the Spatie permission `admin.system`.
 * Drives the KB admin page nav item and the in-page guard. Falls back
 * to checking owner/admin role for dev/seeded environments where
 * permissions are not explicitly assigned.
 */
export function userHasAdminSystem(user: User | null): boolean {
  if (!user) return false;
  if (user.permissions?.some((p) => p.name === 'admin.system')) return true;
  return (user.roles ?? []).some((r) => r.name === 'owner' || r.name === 'admin');
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

/**
 * Returns true when the user has the Spatie `client` role and does NOT
 * also hold any agency-side role (owner|admin|agent|specialist|freelancer).
 * Used by the post-login redirect and by sidebar gating.
 */
export function userIsClient(user: User | null): boolean {
  if (!user || !user.roles?.length) return false;
  const names = user.roles.map((r) => r.name);
  const isClient = names.includes('client');
  const isAgencySide = names.some((n) =>
    ['owner', 'admin', 'agent', 'specialist', 'freelancer'].includes(n),
  );
  return isClient && !isAgencySide;
}

function readStoredUser(): User | null {
  const raw = localStorage.getItem('user');
  if (!raw || raw === 'undefined' || raw === 'null') return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    localStorage.removeItem('user');
    return null;
  }
}

export const useAuth = create<AuthState>((set) => {
  const token = localStorage.getItem('token');
  const user = readStoredUser();

  return {
    user,
    token: token || null,
    isLoading: false,

    login: async (email: string, password: string) => {
      set({ isLoading: true });
      try {
        const res = await api.post('/auth/login', { email, password });
        const { user, token } = res.data;

        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        set({ user, token });
      } finally {
        set({ isLoading: false });
      }
    },

    logout: () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      set({ user: null, token: null });
    },

    refresh: async () => {
      try {
        const res = await api.get('/auth/me');
        const { user } = res.data;
        localStorage.setItem('user', JSON.stringify(user));
        set({ user });
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        set({ user: null, token: null });
      }
    },
  };
});

/**
 * Convenience selector hook: subscribes to the user only and reports
 * whether they should land in the white-label client portal.
 */
export function useIsClient(): boolean {
  return useAuth((s) => userIsClient(s.user));
}
